import { jsxs, jsx } from "react/jsx-runtime";
import classNames from "classnames";
import { memo, useRef, useState, useEffect, useMemo } from "react";
import { LayoutPageAside } from "./LayoutPageAside.js";
import { LayoutPageContent } from "./LayoutPageContent.js";
import { useThemeScheme } from "./ThemeSchemeContext.js";
import { useElementTopOffset } from "./useElementTopOffset.js";
import { useSectionTabs } from "../SectionTabs/Context.js";
import { TitleBar } from "../TitleBar/TitleBar.js";
import { SectionTabs } from "../SectionTabs/SectionTabs.js";
import { useClassNamePrefix } from "../../auxiliary/useClassNamePrefix.js";
import { toThemeClass } from "../../utils/toThemeClass.js";
import { toSchemeClass } from "../../utils/toSchemeClass.js";
import { toEnumClass } from "../../utils/toEnumClass.js";
const LayoutPage = memo(({
  actions,
  afterTitle,
  children,
  fit = "content",
  headingProps,
  layout,
  navigation,
  pageContentLayout,
  side,
  title,
  ...props
}) => {
  const prefix = useClassNamePrefix();
  const {
    scheme,
    theme,
    themeContent,
    themeControls
  } = useThemeScheme(props);
  const contentRef = useRef(null);
  const contentOffsetTop = useElementTopOffset(contentRef);
  const hasTabs = Object.keys(useSectionTabs()).length > 0;
  const [showDivider, setShowDivider] = useState(false);
  useEffect(() => {
    if (!document?.body?.parentElement) {
      return;
    }
    const container = document.body.parentElement;
    const scrollHandler = () => {
      const visibleWidth = container.offsetWidth;
      const contentWidth = container.scrollWidth;
      const scrollLeft = container.scrollLeft;
      setShowDivider(contentWidth > visibleWidth && scrollLeft + visibleWidth < contentWidth);
    };
    scrollHandler();
    window.addEventListener("scroll", scrollHandler, { passive: true });
    return () => {
      window.removeEventListener("scroll", scrollHandler);
    };
  }, []);
  return /* @__PURE__ */ jsxs("div", { className: classNames(
    `${prefix}layout-page`,
    toThemeClass(themeContent ?? theme, themeControls ?? theme),
    toSchemeClass(scheme)
  ), children: [
    (title || actions) && /* @__PURE__ */ jsx(TitleBar, { after: afterTitle === void 0 ? hasTabs ? /* @__PURE__ */ jsx(SectionTabs, {}) : void 0 : afterTitle, navigation, actions, headingProps, children: title }),
    /* @__PURE__ */ jsxs(
      "div",
      {
        ref: contentRef,
        className: classNames(
          `${prefix}layout-page-content-wrap`,
          toEnumClass("fit-", fit),
          showDivider ? "view-aside-divider" : void 0
        ),
        style: useMemo(() => ({ "--cui-content-offset-top": `${contentOffsetTop}px` }), [contentOffsetTop]),
        children: [
          /* @__PURE__ */ jsx(LayoutPageContent, { pageContentLayout: pageContentLayout ?? layout, children }),
          side && /* @__PURE__ */ jsx(LayoutPageAside, { children: side })
        ]
      }
    )
  ] });
});
LayoutPage.displayName = "LayoutPage";
export {
  LayoutPage
};
//# sourceMappingURL=LayoutPage.js.map
